import React, { useState } from "react";
import api from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Sparkles, ServerCog, ShieldCheck } from "lucide-react";

const STEPS = [
  { id: "basics",  label: "Organisation",     icon: ServerCog },
  { id: "admin",   label: "Tenant Admin",     icon: ShieldCheck },
  { id: "config",  label: "Frameworks",       icon: Sparkles },
  { id: "confirm", label: "Review & Provision", icon: Check },
];

const FRAMEWORK_OPTIONS = ["IFRS S1", "IFRS S2", "GRI", "SASB", "GHG Protocol", "ISO 14064-1", "ISO 26000", "TCFD"];
const APPROACHES = [
  { id: "equity_share", label: "Equity Share", blurb: "Account for emissions in proportion to share of equity ownership." },
  { id: "operational", label: "Operational Control", blurb: "Account for 100% of emissions where the org has operational control." },
  { id: "financial", label: "Financial Control", blurb: "Account for 100% of emissions where the org has financial control." },
];

const initial = {
  id: "", name: "", industry: "ITBPM", country: "Philippines",
  headcount: 100, fiscal_year_end: "Dec", baseline_year: 2024, current_period: "2025-Q4",
  admin_email: "", admin_name: "", admin_password: "",
  frameworks: ["IFRS S2", "GRI", "SASB", "GHG Protocol"],
  consolidation_approach: "equity_share",
  notes: "",
};

export default function TenantWizard() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = (idx) => {
    if (idx === 0) {
      if (!form.id || !/^[a-z0-9][a-z0-9-]*$/.test(form.id)) return "Tenant ID must be lowercase alphanumerics + dashes (e.g. acme-bpo).";
      if (!form.name) return "Organisation name is required.";
      if (form.headcount < 1) return "Headcount must be ≥ 1.";
      if (!form.current_period) return "Current period is required.";
    }
    if (idx === 1) {
      if (!form.admin_email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.admin_email)) return "Valid admin email required.";
      if (!form.admin_name) return "Admin name is required.";
      if (!form.admin_password || form.admin_password.length < 8) return "Admin password must be ≥ 8 chars.";
    }
    return "";
  };

  const next = () => {
    const e = validate(step);
    if (e) { setErr(e); return; }
    setErr("");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setErr(""); setStep((s) => Math.max(s - 1, 0)); };

  const provision = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/platform/tenants", form);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Provisioning failed");
    } finally { setBusy(false); }
  };

  const toggleFw = (fw) => {
    const has = form.frameworks.includes(fw);
    update({ frameworks: has ? form.frameworks.filter((x) => x !== fw) : [...form.frameworks, fw] });
  };

  if (result) {
    return (
      <div className="p-6 max-w-3xl" data-testid="wizard-result">
        <div className="card p-6 border-emerald-300 bg-emerald-50/40">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="text-status-success"/></div>
            <div>
              <div className="section-label text-status-success">Tenant provisioned</div>
              <h2 className="text-lg font-semibold mt-0.5">{result.tenant.name}</h2>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <Info label="Tenant ID" value={result.tenant.id}/>
            <Info label="Database" value={result.database}/>
            <Info label="Admin email" value={result.admin_user.email}/>
            <Info label="Role" value={result.admin_user.role}/>
          </div>
          <div className="mt-5">
            <div className="dlabel">Next steps</div>
            <ol className="mt-2 space-y-1 text-sm list-decimal pl-5">
              {result.next_steps.map((s) => <li key={s}>{s}</li>)}
            </ol>
          </div>
          <div className="mt-5 flex gap-2">
            <button data-testid="provision-another" onClick={() => { setResult(null); setForm(initial); setStep(0); }} className="btn btn-secondary">Provision another</button>
            <button data-testid="go-health" onClick={() => nav("/platform/health")} className="btn btn-primary">View tenants health</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="wizard-root">
      <div>
        <div className="section-label">Platform Admin · Provisioning</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Provision New Tenant</h1>
        <p className="text-sm text-ink-soft max-w-3xl">
          Four-step compliant wizard. Creates a dedicated tenant database (database-per-tenant isolation),
          a Tenant Admin user, the Organisation Settings profile (frameworks + GHG Protocol consolidation),
          and an audit-log entry.
        </p>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-4 gap-2" data-testid="stepper">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const active = idx === step;
          const done = idx < step;
          return (
            <div key={s.id} data-testid={`step-${s.id}`} className={"card p-3 " + (active ? "border-brand ring-2 ring-brand/20" : "")}>
              <div className="flex items-center gap-2">
                <div className={"h-7 w-7 rounded-full flex items-center justify-center " + (done ? "bg-emerald-100 text-status-success" : active ? "bg-brand text-white" : "bg-surface text-ink-soft")}>
                  {done ? <Check size={14}/> : <Icon size={14}/>}
                </div>
                <div>
                  <div className="text-[10px] text-ink-soft uppercase tracking-wider">Step {idx + 1}</div>
                  <div className="text-sm font-semibold">{s.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card p-5" data-testid="step-content">
        {step === 0 && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Tenant ID (URL-safe slug)" required>
              <input data-testid="f-id" value={form.id} onChange={(e) => update({ id: e.target.value.toLowerCase() })}
                placeholder="acme-bpo" className="input font-mono"/>
            </Field>
            <Field label="Organisation name" required>
              <input data-testid="f-name" value={form.name} onChange={(e) => update({ name: e.target.value })}
                placeholder="Acme BPO Services, Inc." className="input"/>
            </Field>
            <Field label="Industry">
              <input data-testid="f-industry" value={form.industry} onChange={(e) => update({ industry: e.target.value })} className="input"/>
            </Field>
            <Field label="Country">
              <input data-testid="f-country" value={form.country} onChange={(e) => update({ country: e.target.value })} className="input"/>
            </Field>
            <Field label="Headcount (FTE)">
              <input data-testid="f-headcount" type="number" min="1" value={form.headcount} onChange={(e) => update({ headcount: Number(e.target.value) })} className="input font-mono"/>
            </Field>
            <Field label="Fiscal year end month">
              <input data-testid="f-fye" value={form.fiscal_year_end} onChange={(e) => update({ fiscal_year_end: e.target.value })} placeholder="Dec" className="input"/>
            </Field>
            <Field label="Baseline year">
              <input data-testid="f-baseline" type="number" value={form.baseline_year} onChange={(e) => update({ baseline_year: Number(e.target.value) })} className="input font-mono"/>
            </Field>
            <Field label="Current reporting period">
              <input data-testid="f-period" value={form.current_period} onChange={(e) => update({ current_period: e.target.value })} placeholder="2025-Q4" className="input font-mono"/>
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Tenant Admin name" required>
              <input data-testid="f-admin-name" value={form.admin_name} onChange={(e) => update({ admin_name: e.target.value })} className="input"/>
            </Field>
            <Field label="Tenant Admin email" required>
              <input data-testid="f-admin-email" type="email" value={form.admin_email} onChange={(e) => update({ admin_email: e.target.value })} className="input"/>
            </Field>
            <Field label="Initial password (min 8 chars)" required>
              <input data-testid="f-admin-password" type="text" value={form.admin_password} onChange={(e) => update({ admin_password: e.target.value })} className="input font-mono"/>
            </Field>
            <div className="col-span-2 card-soft p-3 bg-emerald-50/40 text-xs">
              <div className="dlabel">Provisioning grants</div>
              The Tenant Admin can manage Organisation Settings, ESG Units, Factor Libraries, AI &
              Infra Cost Monitors, Cost Alerts & Quotas, and Companies/Sites/Facilities for this tenant only.
              They cannot view other tenants or access the Platform Admin module.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="dlabel mb-2">Adopted reporting frameworks</div>
              <div className="flex flex-wrap gap-2" data-testid="fw-grid">
                {FRAMEWORK_OPTIONS.map((fw) => {
                  const on = form.frameworks.includes(fw);
                  return (
                    <button
                      key={fw}
                      data-testid={`fw-${fw}`}
                      onClick={() => toggleFw(fw)}
                      className={"pill " + (on ? "pill-success" : "")}
                    >
                      {on ? "✓ " : ""}{fw}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="dlabel mb-2">GHG Protocol consolidation approach</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {APPROACHES.map((a) => (
                  <button
                    key={a.id}
                    data-testid={`appr-${a.id}`}
                    onClick={() => update({ consolidation_approach: a.id })}
                    className={"card p-3 text-left " + (form.consolidation_approach === a.id ? "border-brand ring-2 ring-brand/20" : "hover:border-brand-200")}
                  >
                    <div className="font-semibold text-sm">{a.label}</div>
                    <div className="text-[11px] text-ink-soft mt-1">{a.blurb}</div>
                  </button>
                ))}
              </div>
            </div>
            <Field label="Internal notes (optional)">
              <textarea data-testid="f-notes" value={form.notes} onChange={(e) => update({ notes: e.target.value })} className="input h-16"/>
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 text-sm" data-testid="review">
            <div className="dlabel">Review</div>
            <div className="grid grid-cols-2 gap-3">
              <Info label="Tenant ID" value={form.id}/>
              <Info label="Organisation" value={form.name}/>
              <Info label="Country" value={form.country}/>
              <Info label="Headcount" value={form.headcount.toLocaleString()}/>
              <Info label="Baseline" value={form.baseline_year}/>
              <Info label="Period" value={form.current_period}/>
              <Info label="Admin" value={`${form.admin_name} <${form.admin_email}>`}/>
              <Info label="Consolidation" value={form.consolidation_approach.replace("_", " ")}/>
              <Info label="Frameworks" value={form.frameworks.join(", ")} colSpan/>
            </div>
            <div className="card-soft p-3 bg-amber-50 border border-amber-200 text-xs">
              <strong>Confirm provisioning.</strong> A dedicated database
              <code className="font-mono mx-1">…_t_{form.id.replace(/-/g, "_")}</code>
              will be created with empty collections + indexes. The audit log records this action.
            </div>
          </div>
        )}
      </div>

      {err && <div className="text-status-error text-sm" data-testid="wizard-error">{err}</div>}

      {/* Nav */}
      <div className="flex justify-between">
        <button data-testid="wizard-back" disabled={step === 0} onClick={back} className="btn btn-ghost"><ArrowLeft size={12}/> Back</button>
        {step < STEPS.length - 1 && (
          <button data-testid="wizard-next" onClick={next} className="btn btn-primary">Next <ArrowRight size={12}/></button>
        )}
        {step === STEPS.length - 1 && (
          <button data-testid="wizard-submit" disabled={busy} onClick={provision} className="btn btn-primary">
            {busy ? "Provisioning…" : (<><Check size={12}/> Provision tenant</>)}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="dlabel">{label}{required && <span className="text-status-error"> *</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Info({ label, value, colSpan }) {
  return (
    <div className={"card-soft p-2 " + (colSpan ? "col-span-2" : "")}>
      <div className="dlabel">{label}</div>
      <div className="font-medium text-sm mt-0.5 break-words">{value}</div>
    </div>
  );
}
